import { useCallback, useEffect, useMemo, useState } from "react";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { MapaConcretagemForm, MapaConcretagemSchema } from "../schemas/Obras_concretagem/mapa_concretagem";
import { MapaConcretagemDetalhes } from "../../domain/models/mapa_concretagem";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useFornecedorDrawer } from "../../../../common/components/drawers_api/drawers/fornecedor_drawer";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";

import dayjs from "dayjs";

/**
 * LoadMapaConcretagemFuncState
 */

type LoadMapaConcretagemFuncState = {
  idMapaConcretagem?: number;
};

/**
 * useObrasConcretagemCadastroViewModel
 */

export const useObrasConcretagemCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    compras: {
      getAllFornecedorQualificacaoUseCase,
      createFornecedorUseCase,
    },
    obras: {
      getObraHeaderInfoUseCase,
      getMapaConcretagemByIdUseCase,
      createMapaConcretagemUseCase,
      updateMapaConcretagemUseCase,
      downloadMapaConcretagemArquivoUseCase,
    }
  } = useDependencyInjection();

  const {
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const {
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  } = usePageLoader();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idObra, setIdObra] = useState<number>();
  const [idMapaConcretagem, setIdMapaConcretagem] = useState<number>();

  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>();

  const [mapaConcretagem, setMapaConcretagem] = useState<MapaConcretagemDetalhes>();

  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [fornecedorStateDrawer, startFornecedorDrawer] = useFornecedorDrawer();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra });

  const funcionariosAprovacaoDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (mapaConcretagem) {
        if (x.idFuncionario === mapaConcretagem.idFuncionarioAprovado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    mapaConcretagem,
    funcionariosDropdown,
  ]);

  const hookForm = useForm<MapaConcretagemForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(MapaConcretagemSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const ensaioConcretoOptions = useMemo(() => {
    return [
      { id: 0, label: 'Sim' },
      { id: 1, label: 'Não' },
    ];
  }, [
  ]);

  const ensaioModuloElasticidadeOptions = useMemo(() => {
    return [
      { id: 1, label: 'Sim' },
      { id: 0, label: 'Não' },
    ];
  }, [
  ]);

  const aprovadoOptions = useMemo(() => {
    return [
      { id: 1, label: 'Sim' },
      { id: 0, label: 'Não' },
    ];
  }, [
  ]);

  const [
    dataConcretagem,
    aprovado,
    enviadoLaboratorio,
    arquivo1,
    arquivo2,
    arquivo3,
    arquivo4,
    limparArquivo1,
    limparArquivo2,
    limparArquivo3,
    limparArquivo4,
  ] = hookForm.watch([
    'dataConcretagem',
    'aprovado',
    'enviadoLaboratorio',
    'arquivo1',
    'arquivo2',
    'arquivo3',
    'arquivo4',
    'limparArquivo1',
    'limparArquivo2',
    'limparArquivo3',
    'limparArquivo4',
  ]);

  const funcionariosRespAprovDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (mapaConcretagem && mapaConcretagem.idFuncionarioAprovado) {
        if (x.idFuncionario === mapaConcretagem.idFuncionarioAprovado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    funcionariosDropdown,
    mapaConcretagem,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadMapaConcretagem = useCallback(async (fs?: LoadMapaConcretagemFuncState) => {
    const filteredIdMapaConcretagem = fs?.idMapaConcretagem ?? idMapaConcretagem;

    const result = await getMapaConcretagemByIdUseCase.getMapaConcretagemById(
      filteredIdMapaConcretagem,
    );

    hookForm.reset();

    if (result.data) {
      const data = dayjs.utc(result.data).local().toDate();
      hookForm.setValue('dataConcretagem', data);
    }

    hookForm.setValue('tipoConcreto', result.tipoConcreto);
    hookForm.setValue('slupFlowSolicitado', result.slumpSolicit);
    hookForm.setValue('resistMpa', result.resistMpa);
    hookForm.setValue('ensaioConcreto', result.ensaioConcreto);
    hookForm.setValue('moduloResistenciaGPA', result.moduloResistenciaGPA);
    hookForm.setValue('ensaioModuloElasticidade', result.ensaioModuloElasticidade === true ? 1 : (
      result.ensaioModuloElasticidade === false ? 0 : null
    ));

    hookForm.setValue('nroCarro', result.nroCarro);
    hookForm.setValue('nroNotaFiscal', result.nroNotafiscal);

    if (result.saidaUsina) {
      const res = dayjs.duration(result.saidaUsina, 'seconds');
      const minutes = String(res.minutes()).padStart(2, '0');
      const seconds = String(res.seconds()).padStart(2, '0');
      hookForm.setValue('saidaUsina', `${minutes}:${seconds}`);
    }

    if (result.entradaObra) {
      const res = dayjs.duration(result.entradaObra, 'seconds');
      const minutes = String(res.minutes()).padStart(2, '0');
      const seconds = String(res.seconds()).padStart(2, '0');
      hookForm.setValue('entradaObra', `${minutes}:${seconds}`);
    }

    if (result.inicioLanc) {
      const res = dayjs.duration(result.inicioLanc, 'seconds');
      const minutes = String(res.minutes()).padStart(2, '0');
      const seconds = String(res.seconds()).padStart(2, '0');
      hookForm.setValue('inicioLancamento', `${minutes}:${seconds}`);
    }

    if (result.finalLanc) {
      const res = dayjs.duration(result.finalLanc, 'seconds');
      const minutes = String(res.minutes()).padStart(2, '0');
      const seconds = String(res.seconds()).padStart(2, '0');
      hookForm.setValue('finalLancamento', `${minutes}:${seconds}`);
    }

    hookForm.setValue('qteM3', result.qtdeM3);
    hookForm.setValue('slumpFlowEncontrado', result.slump);
    hookForm.setValue('localLancamento', result.localLancamento ?? "");

    if (result.dataEnvio) {
      const data = dayjs(result.dataEnvio).toDate();
      hookForm.setValue('dataEnvioObra', data);
    }

    hookForm.setValue('nroLaudo', result.nroLaudo);
    hookForm.setValue('nroCorpoProva', result.nrocorpoprova);
    hookForm.setValue('nroLote', result.nrocorpoamostra);
    hookForm.setValue('instrucaoLancamento', result.instrucaorompimento);

    if (result.enviadoLaboratorio !== null) {
      hookForm.setValue('enviadoLaboratorio', result.enviadoLaboratorio === true ? 1 : 0);
    }

    if (result.dataEnviadoLaboratorio) {
      const data = dayjs(result.dataEnviadoLaboratorio).toDate();
      hookForm.setValue('dataEnviadoLaboratorio', data);
    }

    hookForm.setValue('resistencia12hs_checkbox', result.checkCompressao12hs);
    hookForm.setValue('resistencia4_checkbox', result.checkCompressao4);
    hookForm.setValue('resistencia7_checkbox', result.checkCompressao7);
    hookForm.setValue('resistencia14_checkbox', result.checkCompressao14);
    hookForm.setValue('resistencia21_checkbox', result.checkCompressao21);
    hookForm.setValue('resistencia28_checkbox', result.checkCompressao28);
    hookForm.setValue('resistencia56_checkbox', result.checkCompressao56);
    hookForm.setValue('resistencia63_checkbox', result.checkCompressao63);

    hookForm.setValue('resistencia12hs', result.resistencia12hs);
    hookForm.setValue('resistencia4', result.resistencia4);
    hookForm.setValue('resistencia7', result.resistencia7);
    hookForm.setValue('resistencia14', result.resistencia14);
    hookForm.setValue('resistencia21', result.resistencia21);
    hookForm.setValue('resistencia28', result.resistencia28);
    hookForm.setValue('resistencia56', result.resistencia56);
    hookForm.setValue('resistencia63', result.resistencia63);

    hookForm.setValue('idade1', result.tempo1);
    hookForm.setValue('resultado1', result.resultado1);
    hookForm.setValue('idade2', result.tempo2);
    hookForm.setValue('resultado2', result.resultado2);
    hookForm.setValue('idade3', result.tempo3);
    hookForm.setValue('resultado3', result.resultado3);

    hookForm.setValue('elasticidade28', result.elasticidade28);
    hookForm.setValue('checkElasticidade28', result.checkElasticidade28);

    if (result.aprovado !== null) {
      hookForm.setValue('aprovado', result.aprovado === true ? 1 : 0);
    }

    if (result.dataAprovacao) {
      const data = dayjs(result.dataAprovacao).toDate();
      hookForm.setValue('dataAprovacao', data);
    }

    hookForm.setValue('observacao', result.justificativa);

    hookForm.setValue('limparArquivo1', false);
    hookForm.setValue('limparArquivo2', false);
    hookForm.setValue('limparArquivo3', false);
    hookForm.setValue('limparArquivo4', false);

    hookForm.setValue('arquivo1', null);
    hookForm.setValue('arquivo2', null);
    hookForm.setValue('arquivo3', null);
    hookForm.setValue('arquivo4', null);

    setMapaConcretagem(result);
  }, [
    idMapaConcretagem,
    hookForm,
    getMapaConcretagemByIdUseCase,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);

      const paramIdObra = params.get('idObra') ?? null;
      const paramIdMapaConc = params.get('id') ?? null;

      const locationIdObra = paramIdObra ? Number(paramIdObra) : undefined;
      const locationIdMapaConcretagem = paramIdMapaConc ? Number(paramIdMapaConc) : undefined;

      if (locationIdObra === undefined) throw new Error('Falha ao obter id da obra!');

      setIdObra(locationIdObra);
      setIdMapaConcretagem(locationIdMapaConcretagem);

      if (locationIdMapaConcretagem === undefined) {
        hookForm.reset({
          ensaioConcreto: 0,
          ensaioModuloElasticidade: 0,
        });
      }
      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadObraHeaderInfo(locationIdObra);

      await loadObrasCommonReportsData(locationIdObra);

      if (locationIdMapaConcretagem !== undefined) {
        await loadMapaConcretagem({ idMapaConcretagem: locationIdMapaConcretagem });
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao carregar dados da pagina!')[0]);
    }
  });

  useEffect(() => {
    (() => {
      if (mapaConcretagem && fornecedoresDropdown) {
        if (mapaConcretagem.idFornecedor || mapaConcretagem.descFornecedor) {
          if (mapaConcretagem.idFornecedor) {
            const fornecedor = fornecedoresDropdown.find(x => x.idFornecedor === mapaConcretagem.idFornecedor);
            hookForm.setValue('fornecedor', fornecedor ?? null);
          }
          if (mapaConcretagem.descFornecedor) {
            hookForm.setValue('fornecedor', mapaConcretagem.descFornecedor);
          }
        }
        if (mapaConcretagem.idFornecedorLaboratio || mapaConcretagem.descLaboratorio) {
          if (mapaConcretagem.idFornecedorLaboratio) {
            const fornecedor = fornecedoresDropdown.find(x => x.idFornecedor === mapaConcretagem.idFornecedorLaboratio);
            hookForm.setValue('laboratorio', fornecedor ?? null);
          }
          if (mapaConcretagem.descLaboratorio) {
            hookForm.setValue('laboratorio', mapaConcretagem.descLaboratorio);
          }
        }
      }
    })();
  }, [
    hookForm,
    mapaConcretagem,
    fornecedoresDropdown,
  ]);

  useEffect(() => {
    (() => {
      if (mapaConcretagem && funcionariosDropdown) {
        if (mapaConcretagem.idFuncionarioAprovado || mapaConcretagem.descResponsavel) {
          if (mapaConcretagem.idFuncionarioAprovado) {
            const funcionario = funcionariosDropdown.find(x => x.idFuncionario === mapaConcretagem.idFuncionarioAprovado);
            hookForm.setValue('responsavelAprovacao', funcionario ?? null);
          } else if (mapaConcretagem.descResponsavel) {
            hookForm.setValue('responsavelAprovacao', mapaConcretagem.descResponsavel);
          }
        }
      }
    })();
  }, [
    hookForm,
    mapaConcretagem,
    funcionariosDropdown,
  ]);

  useEffect(() => {
    (() => {
      if (!enviadoLaboratorio || Number(enviadoLaboratorio) === 0) {
        hookForm.setValue('dataEnviadoLaboratorio', null);
      }
    })();
  }, [
    enviadoLaboratorio,
    hookForm,
  ]);

  useEffect(() => {
    (() => {
      if (!aprovado || Number(aprovado) === 0) {
        hookForm.setValue('dataAprovacao', null);
      }
    })();
  }, [
    aprovado,
    hookForm,
  ]);

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/Obras_concretagem?id=${idObra}` });
  }, [
    idObra,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadObraHeaderInfo(idObra);

      await loadObrasCommonReportsData(idObra);

      if (idMapaConcretagem !== undefined) {
        await loadMapaConcretagem({ idMapaConcretagem });
      } else {
        hookForm.reset({
          ensaioConcreto: 0,
          ensaioModuloElasticidade: 0,
        });
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao carregar dados da pagina!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    idObra,
    idMapaConcretagem,
    hookForm,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadCentroCustoDropDown,
    loadObraHeaderInfo,
    loadObrasCommonReportsData,
    loadMapaConcretagem,
    pushErrorNotification,
  ]);

  const handleMapaConcretagemNew = useCallback(async () => {
    if (idObra) {
      try {
        setLoading(true);

        await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

        await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

        await loadObraHeaderInfo(idObra);

        await loadObrasCommonReportsData(idObra);

        const params = new URLSearchParams(window.location.search);
        params.set('idObra', idObra.toString());
        params.delete('id');

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);

        hookForm.reset({
          ensaioConcreto: 0,
          ensaioModuloElasticidade: 0,
        });

        setIdMapaConcretagem(undefined);
        setMapaConcretagem(undefined);
      } catch (e: unknown) {
        pushErrorNotification('Erro', 'Falha ao iniciar novo mapa de concretagem!');
      }
    }
  }, [
    idObra,
    hookForm,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadObraHeaderInfo,
    loadObrasCommonReportsData,
    pushErrorNotification,
  ]);

  const handleMapaConcretagemSubmit = useCallback(async (form: MapaConcretagemForm) => {
    if (idObra) {
      try {
        setSaving(true);
        setPageSaving(true);
        if (mapaConcretagem) {
          await updateMapaConcretagemUseCase.updateMapaConcretagem(
            mapaConcretagem.idMapaConcretagem,
            form.dataConcretagem,
            form.tipoConcreto,
            form.slupFlowSolicitado,
            form.resistMpa,
            form.ensaioConcreto,
            form.fornecedor,
            form.nroCarro,
            form.nroNotaFiscal,
            form.saidaUsina,
            form.entradaObra,
            form.inicioLancamento,
            form.finalLancamento,
            form.qteM3,
            form.slumpFlowEncontrado,
            form.localLancamento,
            form.dataEnvioObra,
            form.laboratorio,
            form.nroCorpoProva,
            form.nroLote,
            form.instrucaoLancamento,
            form.enviadoLaboratorio === 1 ? true : (form.enviadoLaboratorio === 0 ? false : null),
            form.dataEnviadoLaboratorio,
            form.resistencia12hs,
            form.resistencia12hs_checkbox,
            form.resistencia4,
            form.resistencia4_checkbox,
            form.resistencia7,
            form.resistencia7_checkbox,
            form.resistencia14,
            form.resistencia14_checkbox,
            form.resistencia21,
            form.resistencia21_checkbox,
            form.resistencia28,
            form.resistencia28_checkbox,
            form.resistencia56,
            form.resistencia56_checkbox,
            form.resistencia63,
            form.resistencia63_checkbox,
            form.idade1,
            form.resultado1,
            form.idade2,
            form.resultado2,
            form.idade3,
            form.resultado3,
            form.aprovado === 1 ? true : (form.aprovado === 0 ? false : null),
            form.dataAprovacao,
            form.responsavelAprovacao,
            form.observacao,
            form.arquivo1,
            form.limparArquivo1,
            form.arquivo2,
            form.limparArquivo2,
            form.arquivo3,
            form.limparArquivo3,
            form.arquivo4,
            form.limparArquivo4,
            form.moduloResistenciaGPA,
            form.ensaioModuloElasticidade === 1 ? true : (form.ensaioModuloElasticidade === 0 ? false : null),
            form.nroLaudo,
            form.checkElasticidade28,
            form.elasticidade28,
          );
        } else {
          const createdIdMapaConcretagem = await createMapaConcretagemUseCase.createMapaConcretagem(
            idObra,
            form.dataConcretagem,
            form.tipoConcreto,
            form.slupFlowSolicitado,
            form.resistMpa,
            form.ensaioConcreto,
            form.fornecedor,
            form.nroCarro,
            form.nroNotaFiscal,
            form.saidaUsina,
            form.entradaObra,
            form.inicioLancamento,
            form.finalLancamento,
            form.qteM3,
            form.slumpFlowEncontrado,
            form.localLancamento,
            form.dataEnvioObra,
            form.laboratorio,
            form.nroCorpoProva,
            form.nroLote,
            form.instrucaoLancamento,
            form.enviadoLaboratorio === 1 ? true : (form.enviadoLaboratorio === 0 ? false : null),
            form.dataEnviadoLaboratorio,
            form.resistencia12hs,
            form.resistencia12hs_checkbox,
            form.resistencia4,
            form.resistencia4_checkbox,
            form.resistencia7,
            form.resistencia7_checkbox,
            form.resistencia14,
            form.resistencia14_checkbox,
            form.resistencia21,
            form.resistencia21_checkbox,
            form.resistencia28,
            form.resistencia28_checkbox,
            form.resistencia56,
            form.resistencia56_checkbox,
            form.resistencia63,
            form.resistencia63_checkbox,
            form.idade1,
            form.resultado1,
            form.idade2,
            form.resultado2,
            form.idade3,
            form.resultado3,
            form.aprovado === 1 ? true : (form.aprovado === 0 ? false : null),
            form.dataAprovacao,
            form.responsavelAprovacao,
            form.observacao,
            form.arquivo1,
            form.arquivo2,
            form.arquivo3,
            form.arquivo4,
            form.moduloResistenciaGPA,
            form.ensaioModuloElasticidade === 1 ? true : (form.ensaioModuloElasticidade === 0 ? false : null),
            form.nroLaudo,
            form.checkElasticidade28,
            form.elasticidade28,
          );
          await loadMapaConcretagem({ idMapaConcretagem: createdIdMapaConcretagem });

          const params = new URLSearchParams(window.location.search);
          params.set("id", createdIdMapaConcretagem.toString());

          const newUrl = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, "", newUrl);

          setIdMapaConcretagem(createdIdMapaConcretagem);
        }
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao salvar mapa de concretagem!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idObra,
    mapaConcretagem,
    createMapaConcretagemUseCase,
    updateMapaConcretagemUseCase,
    loadMapaConcretagem,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleMapaConcretagemArquivoDownload = useCallback(async (index: number) => {
    if (mapaConcretagem) {
      try {
        setLoading(true);

        let nomeArquivo = '';
        switch (index) {
          case 1: {
            nomeArquivo = mapaConcretagem.nomeArquivo ?? '';
            break;
          }
          case 2: {
            nomeArquivo = mapaConcretagem.nomeArquivo1 ?? '';
            break;
          }
          case 3: {
            nomeArquivo = mapaConcretagem.nomeArquivo2 ?? '';
            break;
          }
          case 4: {
            nomeArquivo = mapaConcretagem.nomeArquivo3 ?? '';
            break;
          }
        }

        await downloadMapaConcretagemArquivoUseCase.downloadMapaConcretagemArquivo(
          mapaConcretagem.idMapaConcretagem,
          nomeArquivo,
          index,
        );
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    mapaConcretagem,
    downloadMapaConcretagemArquivoUseCase,
    pushErrorNotification,
  ]);

  const handleNewFornecedor = useCallback(() => {
    startFornecedorDrawer(
      async (form) => {
        try {
          setSaving(true);

          const qualificacoes = await getAllFornecedorQualificacaoUseCase
            .getAllFornecedorQualificacao(null);

          if (qualificacoes.length > 0) {
            const defaultQualificacao = [{ idQualificacao: qualificacoes[0].idQualificacao, qualificado: true, observacao: '' }];

            const idFornecedor = await createFornecedorUseCase.createFornecedorSimplificado(
              form.ativo,
              form.avaliarFornecedor,
              form.fornecedorProdutos,
              form.fornecedorServicos,
              form.nomeFantasia,
              form.razaoSocial,
              form.centroCusto,
              form.cnpj,
              form.cpf,
              form.observacoes,
              defaultQualificacao,
            );

            await loadFornecedoresDropDown(dt => {
              setFornecedoresDropdown(dt)
              requestAnimationFrame(() => {
                const fornecedor = dt.find(x => x.idFornecedor === idFornecedor);
                hookForm.setValue('fornecedor', fornecedor ?? null);
              })
            });

            pushInfoNotification('Sucesso', 'Fornecedor criado com sucesso!');
          }
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao criar fornecedor!'));
        } finally {
          setSaving(false);
        }
      },
    );
  }, [
    hookForm,
    createFornecedorUseCase,
    getAllFornecedorQualificacaoUseCase,
    startFornecedorDrawer,
    loadFornecedoresDropDown,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (idMapaConcretagem) {
      changeHistory.handleOpenChangeHistory(idMapaConcretagem, LogTipoTabela.MAPA_CONCRETAGEM, true);
    }
  }, [
    idMapaConcretagem,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idObra,
    idMapaConcretagem,
    selectedObraHeaderInfo,
    obrasTopHeaderState,
    fornecedorStateDrawer,
    obrasReportState,
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    funcionariosDropdown: funcionariosDropdown ?? [],
    funcionariosRespAprovDropdown: funcionariosRespAprovDropdown ?? [],
    centroCustosDropdown: centroCustosDropdown ?? [],
    funcionariosAprovacaoDropdown: funcionariosAprovacaoDropdown ?? [],
    mapaConcretagem: mapaConcretagem ?? null,
    hookForm,
    changeHistory,
    ensaioConcretoOptions,
    ensaioModuloElasticidadeOptions,
    aprovadoOptions,
    dataConcretagem,
    aprovado,
    enviadoLaboratorio,
    arquivo1,
    arquivo2,
    arquivo3,
    arquivo4,
    limparArquivo1,
    limparArquivo2,
    limparArquivo3,
    limparArquivo4,
    handleBack,
    handleReload,
    handleMapaConcretagemNew,
    handleMapaConcretagemSubmit: hookForm.handleSubmit(handleMapaConcretagemSubmit),
    handleMapaConcretagemArquivoDownload,
    handleNewFornecedor,
    handleOpenChangeHistory,
  };
}